/**
 * Pipeline store — Kanban board state for job search tracking.
 * In cloud mode: syncs with backend /api/v1/vacancy.
 * In local mode: persisted to localStorage only.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  PipelineJob,
  PipelineStatus,
  PipelineFilters,
} from '@/types/pipeline';
import { DEFAULT_PIPELINE_FILTERS } from '@/types/pipeline';
import { pipelineApiService } from '@/services/pipelineApi.service';
import { useAuthStore } from './auth.store';

interface PipelineState {
  jobs: PipelineJob[];
  filters: PipelineFilters;
  sortBy: 'score' | 'date' | 'company';
  isLoading: boolean;
  hasFetchedFromBackend: boolean;
}

interface PipelineActions {
  addJob: (data: Omit<PipelineJob, 'id' | 'addedAt' | 'status' | 'notes'>) => PipelineJob;
  moveJob: (jobId: string, newStatus: PipelineStatus) => void;
  removeJob: (jobId: string) => void;
  updateJob: (jobId: string, updates: Partial<PipelineJob>) => void;
  updateJobNotes: (jobId: string, notes: string) => void;
  attachCV: (jobId: string, cvId: string) => void;
  attachCoverLetter: (jobId: string, coverLetterId: string) => void;
  setFilters: (filters: Partial<PipelineFilters>) => void;
  setSortBy: (sortBy: 'score' | 'date' | 'company') => void;
  getJobsByStatus: (status: PipelineStatus) => PipelineJob[];
  getJobById: (jobId: string) => PipelineJob | undefined;
  fetchFromBackend: () => Promise<void>;
  resetFetchFlag: () => void;
  setJobs: (jobs: PipelineJob[]) => void;
}

export type PipelineStore = PipelineState & PipelineActions;

const isCloudMode = () => useAuthStore.getState().isAuthenticated;

export const usePipelineStore = create<PipelineStore>()(
  persist(
    (set, get) => ({
      // State
      jobs: [],
      filters: { ...DEFAULT_PIPELINE_FILTERS },
      sortBy: 'date',
      isLoading: false,
      hasFetchedFromBackend: false,

      // Actions

      /**
       * Fetch all jobs from backend and replace local state.
       * Only runs in cloud mode.
       */
      fetchFromBackend: async () => {
        if (!isCloudMode()) return;

        set({ isLoading: true });
        try {
          const jobs = await pipelineApiService.fetchJobs();
          set({ jobs, hasFetchedFromBackend: true });
        } catch (error) {
          console.error('Failed to fetch pipeline from backend:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      setJobs: (jobs) => set({ jobs }),

      /**
       * Reset fetch flag so next pipeline mount fetches fresh data from backend.
       * Call this on login/logout to avoid stale state.
       */
      resetFetchFlag: () => set({ hasFetchedFromBackend: false }),

      /**
       * Add a job to the pipeline with status 'saved'.
       * In cloud mode: creates in backend, uses returned ID.
       * In local mode: generates UUID locally.
       */
      addJob: (data) => {
        const newJob: PipelineJob = {
          ...data,
          id: crypto.randomUUID(),
          status: 'saved',
          notes: '',
          addedAt: new Date().toISOString(),
        };

        set((state) => ({
          jobs: [...state.jobs, newJob],
        }));

        // Sync to backend (fire-and-forget, replace local ID with server ID)
        if (isCloudMode()) {
          pipelineApiService.createJob({
            title: data.title,
            company: data.company,
            description: data.description,
            url: data.url,
            location: data.location,
            source: data.source,
            matchScore: data.matchScore,
          }).then((serverJob) => {
            // Replace the local job with the server-created one (has real ID)
            set((state) => ({
              jobs: state.jobs.map((j) =>
                j.id === newJob.id ? serverJob : j
              ),
            }));
          }).catch((err) => {
            console.warn('Failed to sync job to backend:', err);
          });
        }

        return newJob;
      },

      /**
       * Move a job to a new pipeline status.
       */
      moveJob: (jobId, newStatus) => {
        set((state) => ({
          jobs: state.jobs.map((job) => {
            if (job.id !== jobId) return job;

            const updates: Partial<PipelineJob> = { status: newStatus };

            if (newStatus === 'applied' && !job.appliedAt) {
              updates.appliedAt = new Date().toISOString();
            }
            if (newStatus === 'interview' && !job.interviewAt) {
              updates.interviewAt = new Date().toISOString();
            }

            return { ...job, ...updates };
          }),
        }));

        // Sync status change to backend
        if (isCloudMode()) {
          pipelineApiService.updateJob(jobId, { status: newStatus }).catch((err) => {
            console.warn('Failed to sync job status:', err);
          });
        }
      },

      /**
       * Remove a job from the pipeline entirely.
       */
      removeJob: (jobId) => {
        set((state) => ({
          jobs: state.jobs.filter((job) => job.id !== jobId),
        }));

        if (isCloudMode()) {
          pipelineApiService.deleteJob(jobId).catch((err) => {
            console.warn('Failed to delete job from backend:', err);
          });
        }
      },

      /**
       * Update any fields on a pipeline job.
       */
      updateJob: (jobId, updates) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId ? { ...job, ...updates } : job
          ),
        }));

        if (isCloudMode() && (updates.status || updates.notes || updates.matchScore !== undefined)) {
          pipelineApiService.updateJob(jobId, {
            status: updates.status,
            notes: updates.notes,
            matchScore: updates.matchScore,
          }).catch((err) => {
            console.warn('Failed to sync job update:', err);
          });
        }
      },

      /**
       * Update the notes field for a specific job.
       */
      updateJobNotes: (jobId, notes) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId ? { ...job, notes } : job
          ),
        }));

        if (isCloudMode()) {
          pipelineApiService.updateJob(jobId, { notes }).catch((err) => {
            console.warn('Failed to sync notes:', err);
          });
        }
      },

      /**
       * Attach a generated CV to a pipeline job.
       */
      attachCV: (jobId, cvId) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId ? { ...job, generatedCVId: cvId } : job
          ),
        }));
      },

      /**
       * Attach a cover letter to a pipeline job.
       */
      attachCoverLetter: (jobId, coverLetterId) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId ? { ...job, coverLetterId } : job
          ),
        }));
      },

      /**
       * Update filters (partial merge).
       */
      setFilters: (filters) => {
        set((state) => ({
          filters: { ...state.filters, ...filters },
        }));
      },

      /**
       * Set the sort order for pipeline jobs.
       */
      setSortBy: (sortBy) => {
        set({ sortBy });
      },

      /**
       * Get all jobs filtered by a specific pipeline status.
       */
      getJobsByStatus: (status) => {
        return get().jobs.filter((job) => job.status === status);
      },

      /**
       * Get a single job by its ID.
       */
      getJobById: (jobId) => {
        return get().jobs.find((job) => job.id === jobId);
      },
    }),
    {
      name: 'pipeline-storage',
      partialize: (state) => ({
        jobs: state.jobs,
        filters: state.filters,
        sortBy: state.sortBy,
      }),
    }
  )
);
